import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import api from '../../../utils/api';

const plans = [
  {
    id: 'single',
    title: 'Starter',
    tagline: '1 Interview',
    features: [
      'AI mock interview access',
      'Detailed performance reports',
      'Coding evaluation',
      'Strength & weakness analysis',
      'Interview recordings/transcripts',
      'Resume-based personalization',
    ],
    cta: 'Choose Starter',
  },
  {
    id: 'pack3',
    title: 'Sprint',
    tagline: '3 Interviews',
    features: [
      'AI mock interview access',
      'Detailed performance reports',
      'Coding evaluation',
      'Strength & weakness analysis',
      'Interview recordings/transcripts',
      'Resume-based personalization',
    ],
    cta: 'Choose Sprint',
    featured: true,
  },
  {
    id: 'pack5',
    title: 'Pro',
    tagline: '5 Interviews',
    features: [
      'AI mock interview access',
      'Detailed performance reports',
      'Coding evaluation',
      'Strength & weakness analysis',
      'Interview recordings/transcripts',
      'Resume-based personalization',
    ],
    cta: 'Choose Pro',
  },
];

const PricingSection = () => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = React.useState(null);
  const [campaign, setCampaign] = React.useState(null);

  React.useEffect(() => {
    api.get('/api/public/plans', { params: { _t: Date.now() } })
      .then((response) => { setCatalog(response.data?.plans || []); setCampaign(response.data?.campaign || null); })
      .catch(() => setCatalog([]));
  }, []);

  const visiblePlans = (catalog || []).map((plan) => ({
    ...(plans.find((fallback) => fallback.id === plan.key) || { features: [], cta: `Choose ${plan.name}` }),
    id: plan.key,
    title: plan.name,
    price: `₹${Math.round(Number(plan.price_paise || 0) / 100)}`,
    originalPrice: `₹${Math.round(Number(plan.original_price_paise || plan.price_paise || 0) / 100)}`,
    hasDiscount: Number(plan.discount_amount_paise || 0) > 0,
    period: Number(plan.discount_amount_paise || 0) > 0 ? `${campaign?.name || plan.campaign_name || 'Offer'} · ${Math.round(Number(plan.discount_amount_paise) * 100 / Number(plan.original_price_paise || 1))}% off` : '',
  }));

  return (
    <section id="pricing" className="relative border-t border-[var(--lp-line)] bg-[var(--lp-bg)] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p className="font-mono text-[11px] font-semibold tracking-[0.2em] text-[var(--lp-accent)]">
            Pricing
          </p>
          <h2 className="mt-4 font-semibold text-4xl text-[var(--lp-ink)] sm:text-5xl">
            Interview packs for every stage of prep.
          </h2>
          <p className="mt-5 text-base leading-7 text-[var(--lp-ink-soft)]">
            Pick the number of AI mock interviews you need, from a fast baseline check to a full offer-focused practice run.
          </p>
        </div>

        <div className="mt-14 grid gap-5 lg:grid-cols-3">
          {visiblePlans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              className={`relative flex flex-col rounded-xl border p-6 transition-shadow ${
                plan.featured
                  ? 'border-[var(--lp-contrast-bg)] bg-[var(--lp-contrast-bg)] text-[var(--lp-contrast-text)] shadow-[0_30px_80px_-25px_rgba(0,0,0,0.2)]'
                  : 'border-[var(--lp-line)] bg-[var(--lp-surface)] text-[var(--lp-ink)]'
              }`}
            >
              {plan.featured && (
                <span className="absolute -top-3 left-6 rounded-full bg-[var(--lp-accent)] px-3 py-1 font-mono text-[10px] font-bold tracking-wider text-[var(--lp-ink)]">
                  Most Popular
                </span>
              )}

              <p className="font-mono text-[10px] font-bold tracking-widest text-[var(--lp-accent)]">
                {plan.tagline}
              </p>
              <h3 className={`mt-2 font-semibold text-3xl ${plan.featured ? 'text-[var(--lp-contrast-text)]' : 'text-[var(--lp-ink)]'}`}>
                {plan.title}
              </h3>

              <div className="mt-6 flex items-baseline gap-2">
                {plan.hasDiscount && <span className={`text-base line-through ${plan.featured ? 'text-[var(--lp-contrast-text)]/50' : 'text-[var(--lp-ink-soft)]'}`}>{plan.originalPrice}</span>}
                <span className="font-semibold text-4xl">{plan.price}</span>
                {plan.hasDiscount && <span className={`text-sm ${plan.featured ? 'text-[var(--lp-contrast-text)]/50' : 'text-[var(--lp-ink-soft)]'}`}>{plan.period}</span>}
              </div>

              <ul className="mt-8 flex-1 space-y-3">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Icon
                      name="Check"
                      size={14}
                      className={`mt-0.5 shrink-0 ${plan.featured ? 'text-[var(--lp-accent)]' : 'text-[var(--lp-accent)]'}`}
                      strokeWidth={3}
                    />
                    <span className={plan.featured ? 'text-[var(--lp-contrast-text)]/80' : 'text-[var(--lp-ink-soft)]'}>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                size="lg"
                onClick={() => navigate('/checkout', { state: { preselect: 'coin', planId: plan.id } })}
                className="mt-7 h-11 w-full rounded-full bg-[var(--lp-accent)] text-sm font-semibold text-white hover:bg-[var(--lp-accent)]/90"
                iconName="ArrowRight"
                iconPosition="right"
              >
                {plan.cta}
              </Button>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default PricingSection;
